import { ComponentType, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { SidePanelType } from 'features/side-panels/types';
import { loadPanelView } from 'features/side-panels/loadPanelView';
import { usePanelRecovery } from 'features/side-panels/hooks/usePanelRecovery';

export type PanelEntry = { Panel: ComponentType; id: string };

type UseLazyPanelsParams = {
  activePanel: SidePanelType | null;
  pinnedPanels: SidePanelType[];
};

export const useLazyPanels = ({ activePanel, pinnedPanels }: UseLazyPanelsParams) => {
  const [panels, setPanels] = useState<Partial<Record<SidePanelType, PanelEntry>>>({});
  const prevVisibleRef = useRef<Set<SidePanelType>>(new Set());
  const panelsRef = useRef(panels);
  panelsRef.current = panels;

  const visible = useMemo(() => {
    const set = new Set<SidePanelType>();
    if (activePanel) {
      set.add(activePanel);
    }
    pinnedPanels.forEach((panelName) => set.add(panelName));
    return set;
  }, [activePanel, pinnedPanels]);

  const loadPanel = useCallback((panelName: SidePanelType, force = false) => {
    if (!force && panelsRef.current[panelName]) {
      return;
    }

    const Panel = loadPanelView(panelName);
    setPanels((prev) => ({ ...prev, [panelName]: { Panel, id: uuidv4() } }));
  }, []);

  const reload = useCallback((panelName: SidePanelType) => loadPanel(panelName, true), [loadPanel]);

  const { onError, onLoaded, retryNow, isExhausted } = usePanelRecovery({ visible, reload });

  useEffect(() => {
    visible.forEach((panelName) => {
      loadPanel(panelName);
      if (!prevVisibleRef.current.has(panelName)) {
        retryNow(panelName);
      }
    });

    prevVisibleRef.current = visible;
  }, [loadPanel, visible, retryNow]);

  return { panels, onError, onLoaded, isExhausted };
};
